import {
	type BaseLayoutItem,
	type Panel,
	type Field,
	type InactiveFieldLayoutItem,
	type PanelLayoutItem,
	FIELD_LAYOUT_ITEM_TYPE,
	PANEL_LAYOUT_ITEM_TYPE,
	toLayoutItemKey,
} from '../../types/layout-items.tsx';
import type { FieldItemFragment as GraphqlFieldItem } from '../fragments/__generated_apollo__/FieldItemFragment';
import type { PanelItemFragment as GraphqlPanelItem } from '../fragments/__generated_apollo__/PanelItemFragment';
import type { BaseLayoutItemTransformer, Transformers } from './types.tsx';

const fieldToLayoutItem = (field: Field): InactiveFieldLayoutItem => ({
	key: toLayoutItemKey(FIELD_LAYOUT_ITEM_TYPE, field.key),
	layoutItemType: FIELD_LAYOUT_ITEM_TYPE,
	data: field,
	operations: field.operations,
	category: null,
});

const panelToLayoutItem = (panel: Panel): PanelLayoutItem => ({
	key: toLayoutItemKey(PANEL_LAYOUT_ITEM_TYPE, panel.key),
	layoutItemType: PANEL_LAYOUT_ITEM_TYPE,
	data: panel,
	operations: panel.operations,
	category: null,
});

/**
 * Transform layout item data from GraphQL into SWIFT compatible {@link BaseLayoutItem} objects.
 */
export const transformBaseLayoutItems: BaseLayoutItemTransformer = (
	layoutItemNodes: ReadonlyArray<GraphqlFieldItem | GraphqlPanelItem>,
	transformers: Transformers,
): BaseLayoutItem[] =>
	layoutItemNodes.reduce<Array<BaseLayoutItem>>(
		(accum: BaseLayoutItem[], node: GraphqlFieldItem | GraphqlPanelItem) => {
			let layoutItem: BaseLayoutItem;
			switch (node.__typename) {
				case 'JiraIssueLayoutFieldItemConfiguration': {
					const field = transformers.field(node, transformers);
					layoutItem = fieldToLayoutItem(field);
					break;
				}
				case 'JiraIssueLayoutPanelItemConfiguration': {
					const panel = transformers.panel(node, transformers);
					layoutItem = panelToLayoutItem(panel);
					break;
				}
				default:
					// We ignore all non-base layout items (i.e. items we don't want to render in the sidebar).
					return accum;
			}

			return accum.concat([layoutItem]);
		},
		[],
	);
